import axios from 'axios'
import store from '@/store'

const instance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error)
    else prom.resolve(token)
  })
  failedQueue = []
}

// Helper: get current refresh token and endpoint
const getRefreshData = () => {
  if (store.getters['superAdminAuthModule/getIsAuthenticated']) {
    return {
      refreshToken: store.getters['superAdminAuthModule/getRefreshToken'],
      endpoint: `${process.env.VUE_APP_API_URL}/api/super-admin/refresh`,
      module: 'superAdminAuthModule',
    }
  } if (store.getters['authModule/getIsAuthenticated']) {
    return {
      refreshToken: store.getters['authModule/getRefreshToken'],
      endpoint: `${process.env.VUE_APP_API_URL}/api/refresh`,
      module: 'authModule',
    }
  }
  return null
}

// Request interceptor → attach access token
instance.interceptors.request.use(
  config => {
    const skipUrls = ['/api/refresh', '/api/super-admin/refresh']
    if (skipUrls.some(url => config.url.endsWith(url))) return config

    let token = ''
    if (store.getters['superAdminAuthModule/getIsAuthenticated']) {
      token = store.getters['superAdminAuthModule/getToken']
    } else if (store.getters['authModule/getIsAuthenticated']) {
      token = store.getters['authModule/getToken']
    }

    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  },
  error => Promise.reject(error),
)

// Response interceptor → handle 401
instance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    if (error.response?.status === 401 && originalRequest._retry) {
      // Already retried → logout
      redirectToLogin()
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      const refreshData = getRefreshData()

      if (!refreshData || !refreshData.refreshToken) {

        redirectToLogin()
        return Promise.reject(error)
      }

      if (isRefreshing) {

        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return instance(originalRequest)
        })
      }
      originalRequest._retry = true
      isRefreshing = true

      try {
        // Bypass interceptors
        const res = await axios.post(
          refreshData.endpoint,
          {},
          {
            headers: { Authorization: `Bearer ${refreshData.refreshToken}` },
            withCredentials: true,
          },
        )
        const newAccessToken = res.data.access_token
        const newRefreshToken = res.data.refresh_token || refreshData.refreshToken

        // Update store
        await store.dispatch(`${refreshData.module}/setIsAuthenticated`, {
          isAuthenticated: true,
          token: newAccessToken,
          refreshToken: newRefreshToken,
        })

        // Update Axios defaults
        instance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`

        processQueue(null, newAccessToken)

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return instance(originalRequest)
      } catch (err) {
        processQueue(err, null)
        redirectToLogin()
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

function redirectToLogin() {

  // Determine who is logged in first
  const isSuperAdmin = store.getters['superAdminAuthModule/getIsAuthenticated']
  const isUser = store.getters['authModule/getIsAuthenticated']

  // Clear super admin
  store.dispatch('superAdminAuthModule/setIsAuthenticated', { isAuthenticated: false, token: null, refreshToken: null })
  store.dispatch('superAdminAuthModule/clearPersistedState')

  // Clear normal user
  store.dispatch('authModule/setIsAuthenticated', { isAuthenticated: false, token: null, refreshToken: null })
  store.dispatch('authModule/clearPersistedState')
  store.dispatch('userModule/removeUser')

  // Redirect based on who was logged in
  if (isSuperAdmin) {
    window.location.href = '/super-admin-login'
  } else if (isUser) {
    window.location.href = '/'
  } else {
    // Fallback if somehow no one was logged in
    window.location.href = '/'
  }
}

export default instance
